/**
 * translationSegmentation (client mirror) — structural segmentation for
 * whole-document translation (2026-08-11, INCIDENT-20260811 capacity half).
 *
 * MIRROR of `segmentSourceForTranslation` in
 * functions/src/prest0nSession/toolRouter.ts. The frontend bundle cannot import
 * from functions/, so the algorithm is duplicated here and
 * tests/tools/translationSegmentationLockstep.test.ts runs BOTH copies over the
 * same battery and fails on any behavioural difference.
 *
 * Why the browser needs it at all: the customer path for a 200-page expediente
 * is the in-tab translator, not the Cloud Function. Sequential continuation
 * passes cannot finish that document inside any sane wall clock — ~21 segments
 * at ~2 minutes each is over 40 minutes. Segmenting lets the client fan the
 * work out concurrently and reassemble in order, which is what makes
 * "translate the whole thing, in one request" physically possible.
 */

/** Segment size: large enough that a 200-page document is tens (not hundreds)
 *  of calls, small enough that one segment's translation fits comfortably
 *  inside a single model response. Keep in lockstep with the server copy. */
export const TRANSLATION_SEGMENT_CHARS = 24_000;

/**
 * Split a source document into translation-sized segments on STRUCTURAL
 * boundaries (lines/paragraphs first, sentences next, hard cut only as a last
 * resort), so headings, numbering and paragraph shape survive reassembly.
 */
export function segmentSourceForTranslation(
  text: string,
  maxChars: number = TRANSLATION_SEGMENT_CHARS,
): string[] {
  const src = String(text || '');
  if (!src.trim()) return [];
  if (src.length <= maxChars) return [src];

  const segments: string[] = [];
  let current = '';
  const flush = () => {
    if (current.length > 0) {
      segments.push(current);
      current = '';
    }
  };

  for (const line of src.split('\n')) {
    const candidate = current.length ? `${current}\n${line}` : line;
    if (candidate.length <= maxChars) {
      current = candidate;
      continue;
    }
    flush();
    if (line.length <= maxChars) {
      current = line;
      continue;
    }
    for (const piece of splitOversizedLine(line, maxChars)) segments.push(piece);
  }
  flush();
  return segments;
}

function splitOversizedLine(line: string, maxChars: number): string[] {
  const out: string[] = [];
  let current = '';
  for (const sentence of line.split(/(?<=[.!?])\s+/)) {
    const candidate = current.length ? `${current} ${sentence}` : sentence;
    if (candidate.length <= maxChars) {
      current = candidate;
      continue;
    }
    if (current.length) {
      out.push(current);
      current = '';
    }
    if (sentence.length <= maxChars) {
      current = sentence;
      continue;
    }
    for (let i = 0; i < sentence.length; i += maxChars) out.push(sentence.slice(i, i + maxChars));
  }
  if (current.length) out.push(current);
  return out;
}

// ── Manifest-aware splitting ──────────────────────────────────────────────────
//
// The translator is handed a MANIFEST (===TITLE===, ===BODY_CONTENT=== …), not
// raw prose, and the caller parses the translated manifest back out. Segmenting
// the manifest naively would shred those delimiters and every downstream
// deliverable with them, so the body is separated first and only the body is
// segmented; the header sections ride the first call and come back translated
// exactly as before.

const BODY_MARKER = '===BODY_CONTENT===';
const END_MARKER = '===END===';

export interface ManifestParts {
  /** Everything before ===BODY_CONTENT=== (markers included). */
  header: string;
  /** The body text between the body marker and ===END===. */
  body: string;
  /** Trailing text after the body (normally just ===END===). */
  tail: string;
}

/**
 * Split manifest text into header / body / tail. Returns null when the input is
 * not manifest-shaped (a plain string of prose), so callers can treat the whole
 * input as body.
 */
export function splitManifestForTranslation(manifestText: string): ManifestParts | null {
  const text = String(manifestText || '');
  const bodyIdx = text.indexOf(BODY_MARKER);
  if (bodyIdx === -1) return null;
  const bodyStart = bodyIdx + BODY_MARKER.length;
  const endIdx = text.indexOf(END_MARKER, bodyStart);
  return {
    header: text.slice(0, bodyStart),
    body: endIdx === -1 ? text.slice(bodyStart) : text.slice(bodyStart, endIdx),
    tail: endIdx === -1 ? '' : text.slice(endIdx),
  };
}

/** Rebuild manifest text with a replacement body — the inverse of the split. */
export function rejoinManifest(parts: ManifestParts, body: string): string {
  const tail = parts.tail || `\n${END_MARKER}`;
  return `${parts.header}\n${body.trim()}\n${tail.trimStart()}`;
}
